import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const RETURN_TYPES = ["1040", "1065", "1120", "1120S", "990", "1041", "other"];
const COMPLEXITIES = ["simple", "standard", "complex"];
const WORK_TYPES = [
  { value: "tax_return", label: "Tax return" },
  { value: "bookkeeping", label: "Bookkeeping" },
  { value: "other", label: "Other" },
];

const blank = () => ({
  client_name: "",
  client_id: "",
  work_type: "tax_return",
  work_type_other: "",
  return_type: "1040",
  tax_year: new Date().getFullYear() - 1,
  period: "",
  complexity: "standard",
  est_hours: "",
  due_date: "",
  notes: "",
});

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export const ReturnFormDialog = ({ open, onOpenChange, onSubmit, busy, editing, currentUserName }) => {
  const [form, setForm] = useState(blank());

  useEffect(() => {
    if (open) {
      setForm(
        editing
          ? {
              ...blank(),
              ...editing,
              work_type: editing.work_type || "tax_return",
              work_type_other: editing.work_type_other || "",
              return_type: editing.return_type || "1040",
              tax_year: editing.tax_year ?? new Date().getFullYear() - 1,
              period: editing.period || "",
              client_id: editing.client_id || "",
              est_hours: editing.est_hours ?? "",
              due_date: editing.due_date || "",
              notes: editing.notes || "",
            }
          : blank()
      );
    }
  }, [open, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addedBy = editing?.created_by || currentUserName || "You";
  const isTax = form.work_type === "tax_return";
  const isOther = form.work_type === "other";

  const valid =
    form.client_name.trim() &&
    (isTax ? !!form.tax_year : form.period.trim()) &&
    (!isOther || form.work_type_other.trim());

  const submit = () =>
    onSubmit({
      ...form,
      work_type_other: isOther ? form.work_type_other.trim() : null,
      return_type: isTax ? form.return_type : null,
      tax_year: isTax ? parseInt(form.tax_year, 10) : null,
      period: isTax ? null : form.period.trim(),
      est_hours: form.est_hours === "" ? null : parseFloat(form.est_hours),
      client_id: form.client_id || null,
      due_date: form.due_date || null,
      notes: form.notes || null,
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="return-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit work" : "Put work on the shelf"}</DialogTitle>
          <DialogDescription>
            {editing
              ? "Update the details on this job."
              : "Anything you can't get to — a return, a month of bookkeeping, whatever. It goes straight on the shelf for whoever has time."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Client name">
            <Input
              value={form.client_name}
              onChange={(e) => set("client_name", e.target.value)}
              placeholder="Smith Family Trust"
              data-testid="return-client-name-input"
            />
          </Field>
          <Field label="Client ID (optional)">
            <Input
              value={form.client_id}
              onChange={(e) => set("client_id", e.target.value)}
              placeholder="Firm360 / Lacerte ref"
              data-testid="return-client-id-input"
            />
          </Field>

          <Field label="Type of work">
            <Select value={form.work_type} onValueChange={(v) => set("work_type", v)}>
              <SelectTrigger data-testid="work-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WORK_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {isOther && (
            <Field label="What kind of work?">
              <Input
                value={form.work_type_other}
                onChange={(e) => set("work_type_other", e.target.value)}
                placeholder="Payroll, sales tax, IRS notice…"
                data-testid="work-type-other-input"
              />
            </Field>
          )}

          {isTax ? (
            <>
              <Field label="Return type">
                <Select value={form.return_type} onValueChange={(v) => set("return_type", v)}>
                  <SelectTrigger data-testid="return-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RETURN_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Tax year">
                <Input
                  type="number"
                  value={form.tax_year}
                  onChange={(e) => set("tax_year", e.target.value)}
                  data-testid="return-tax-year-input"
                />
              </Field>
            </>
          ) : (
            <Field label="Period covered">
              <Input
                value={form.period}
                onChange={(e) => set("period", e.target.value)}
                placeholder="Aug 2026, Q3 2026, Jan–Jun 2026…"
                data-testid="period-input"
              />
            </Field>
          )}

          <Field label="Complexity">
            <Select value={form.complexity} onValueChange={(v) => set("complexity", v)}>
              <SelectTrigger data-testid="return-complexity-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COMPLEXITIES.map((c) => (
                  <SelectItem key={c} value={c} className="capitalize">
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Est. hours (optional)">
            <Input
              type="number"
              step="0.5"
              value={form.est_hours}
              onChange={(e) => set("est_hours", e.target.value)}
              data-testid="return-est-hours-input"
            />
          </Field>
          <Field label="Due date">
            <Input
              type="date"
              value={form.due_date}
              onChange={(e) => set("due_date", e.target.value)}
              data-testid="return-due-date-input"
            />
          </Field>
          <Field label="Put on the shelf by">
            <Input value={addedBy} disabled readOnly data-testid="added-by-display" />
          </Field>

          <Field label="Notes" className="sm:col-span-2">
            <Textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Where it stands, what's left, anything the next person needs"
              data-testid="return-notes-input"
            />
          </Field>

        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="return-form-cancel-btn">
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || !valid} data-testid="return-form-submit-btn">
            {busy ? "Saving…" : editing ? "Save changes" : "Add work"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
